import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { usePrice } from "../hooks/usePrice";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLanguage, useT } from "../i18n/LanguageProvider";
import { LogoImage, LogoMark } from "./BrandLogo";
import { MobileBottomNav } from "./MobileBottomNav";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/notifications";
import { C, font } from "../styles/tokens";
import { formatRupees } from "../utils/money";
import { officerRoleLabel } from "../utils/accountRole";
import { isDisplayableUrl } from "../utils/imageUrl";
import { LEGAL_CONFIG } from "../config/legalConfig";

import { PortalTabs } from "./PortalTabs";
import { InlineIcon } from "./InlineIcon";
import { OrganisationProfileModal } from "./OrganisationProfileModal";

/**
 * AppShell - the top navigation bar shared by all protected pages.
 *
 * Layout (left to right):
 *   Logo (with Admin badge if PLATFORM_ADMIN)  Nav items
 *   ... right side: notifications bell, language toggle, avatar menu
 *
 * Mobile (<700px): nav collapses into a hamburger; avatar menu is full-width on tap.
 * The bottom-nav-on-mobile pattern is deferred - top nav reflows for now.
 */
export function AppShell({ children, maxWidth = 1440, dense = false }) {
  const { user: localUser } = useAuth();
  const t = useT();
  const location = useLocation();
  const isMobile = useIsMobile();
  const userType = localUser?.type;
  const isVerifier =
    userType === "INSTITUTION_OFFICER" || userType === "ORGANISATION_OFFICER";

  const navItems = [];
  if (userType === "PROFESSIONAL") {
    navItems.push({ to: "/profile", label: t("nav.profile") });
    navItems.push({ to: "/verifications", label: t("nav.verifications") });
    navItems.push({ to: "/billing", label: t("nav.billing") });
    //navItems.push({ to: "/search-jobs", label: t("nav.talentHub") });
  } else if (
    userType === "INSTITUTION_OFFICER" ||
    userType === "ORGANISATION_OFFICER"
  ) {
    navItems.push({ to: "/dashboard", label: t("nav.dashboard") });
    // Both officer types decide requests from the inbox. Organisation
    // officers previously had NO path to /inbox (the dashboard's "See all"
    // link was institution-only), leaving them without Approve/Reject.
    navItems.push({ to: "/inbox", label: t("nav.inbox") });
    navItems.push({ to: "/officers", label: "Officers" });
    if (userType === "ORGANISATION_OFFICER") {
      //navItems.push({
      //  to: "/post-opportunity",
      //  label: t("nav.postOpportunity"),
      //});
      //navItems.push({ to: "/candidates", label: "Candidates" });
    }
    navItems.push({ to: "/earnings", label: t("nav.billingEarnings") });
  } else if (userType === "PLATFORM_ADMIN") {
    navItems.push({ to: "/admin", label: t("nav.overview") });
    navItems.push({
      to: "/admin/pending",
      label: t("nav.pendingApplications"),
    });
    navItems.push({ to: "/admin/audit-log", label: t("nav.auditLog") });
    navItems.push({ to: "/admin/coupons", label: "Coupons" });
    navItems.push({ to: "/admin/legal", label: "Terms & Legal" });
  }

  const homeLink =
    userType === "PROFESSIONAL"
      ? "/profile"
      : userType === "PLATFORM_ADMIN"
        ? "/admin"
        : "/dashboard";

  const activeFor = (to) => {
    if (location.pathname === to) return true;
    if (to === "/profile" && location.pathname.startsWith("/credentials"))
      return true;
    if (to === "/verifications" && location.pathname.startsWith("/verify/"))
      return true;
    if (to === "/inbox" && location.pathname.startsWith("/inbox")) return true;
    if (
      to === "/admin/pending" &&
      location.pathname.startsWith("/admin/pending")
    )
      return true;
    if (to === "/search-jobs" && location.pathname.startsWith("/search-jobs"))
      return true;
    return false;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: C.bgMuted,
        fontFamily: font,
        color: C.textDark,
        display: "flex",
        flexDirection: "column",
      }}
      className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col w-full overflow-x-hidden"
    >
      <TopBar
        homeLink={homeLink}
        navItems={navItems}
        activeFor={activeFor}
        isAdmin={userType === "PLATFORM_ADMIN"}
      />
      <main
        className={`flex-1 w-full mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 ${maxWidth === "100%" ? "" : "max-w-[1440px]"}`}
        style={{
          flex: 1,
          width: "100%",
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
          margin:
            maxWidth === "100%" ? "0 auto" : dense ? "6px auto" : "20px auto",
          boxSizing: "border-box",
          padding:
            maxWidth === "100%"
              ? isMobile
                ? "16px 12px"
                : "24px 32px"
              : undefined,
          paddingBottom:
            isMobile && userType === "PROFESSIONAL" ? 72 : undefined,
        }}
      >
        {children}
      </main>

      {/* On mobile-Professional the fixed MobileBottomNav serves as the
          bottom anchor; the footer would visually overlap it. Skip it there. */}
      {!(isMobile && userType === "PROFESSIONAL") && <Footer />}

      {/* Fixed bottom tab bar -- Professional role, mobile only */}
      {isMobile && userType === "PROFESSIONAL" && <MobileBottomNav />}
    </div>
  );
}

// =============================================================================
// Footer -- small, muted strip so short pages don't end in a wall of empty space
// =============================================================================

function Footer() {
  const isMobile = useIsMobile();
  const year = new Date().getFullYear();
  return (
    <footer
      style={{
        width: "100%",
        borderTop: `1px solid ${C.divider}`,
        background: C.bg,
        padding: isMobile ? "24px 16px" : "32px 40px",
        fontFamily: font,
        fontSize: 12,
        color: C.textLight,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "flex-end",
          gap: isMobile ? 24 : 32,
        }}
      >
        {/* Bottom Corner Left: Logo, Company Name, Address */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxWidth: 420,
          }}
        >
          <div>
            <LogoImage variant="lockup" height={28} />
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.navy,
              letterSpacing: "-0.2px",
            }}
          >
            {LEGAL_CONFIG.companyName}
          </div>
          <div style={{ color: C.textMed, lineHeight: 1.5, fontSize: 12 }}>
            {LEGAL_CONFIG.businessAddress}
          </div>
        </div>

        {/* Middle: Copyright Notice */}
        <div
          style={{
            fontSize: 11,
            color: C.textLight,
            textAlign: isMobile ? "left" : "center",
            paddingBottom: isMobile ? 0 : 2,
          }}
        >
          &copy; {year} TickAndPass - India's credentials verification platform
        </div>

        {/* Bottom Right: Footer Links & Support */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "flex-start" : "flex-end",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <FooterLink to="/terms-and-conditions">
              Terms & Conditions
            </FooterLink>
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
          </div>
          <div>
            <a href="mailto:support@tickandpass.com" style={footerLinkStyle}>
              support@tickandpass.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} style={footerLinkStyle}>
      {children}
    </Link>
  );
}

const footerLinkStyle = {
  color: C.textMed,
  textDecoration: "none",
  fontWeight: 500,
};

// =============================================================================
// Top bar
// =============================================================================

function TopBar({ homeLink, navItems, activeFor, isAdmin }) {
  const t = useT();
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadCount() {
      try {
        const { unread } = await fetchUnreadCount();
        if (!cancelled) setInboxUnread(unread ?? 0);
      } catch {
        /* silent catch */
      }
    }
    loadCount();
    const interval = setInterval(loadCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 150,
          background: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
          padding: isMobile ? "10px 16px" : "10px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 12 : 28,
            minWidth: 0,
          }}
        >
          <Link
            to={homeLink}
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: C.navy,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              letterSpacing: -0.3,
              flexShrink: 0,
            }}
          >
            {isMobile ? (
              <LogoMark height={22} />
            ) : (
              <LogoImage variant="lockup" height={30} />
            )}
            {isAdmin && !isMobile && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.amber,
                  background: C.amberLight,
                  padding: "2px 8px",
                  borderRadius: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  marginLeft: 4,
                }}
              >
                {t("nav.admin")}
              </span>
            )}
          </Link>
          {/* Inline nav on desktop only. */}
          {!isMobile && (
            <nav style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {navItems.map((item) => {
                const active = activeFor(item.to);
                const isInbox = item.to === "/inbox";
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    style={{
                      padding: "8px 16px",
                      fontSize: 14,
                      fontWeight: active ? 700 : 500,
                      color: active ? C.primary : C.textMuted,
                      textDecoration: "none",
                      borderRadius: 999,
                      background: active ? C.primaryTint : "transparent",
                      fontFamily: font,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span>{item.label}</span>
                    {isInbox && inboxUnread > 0 && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#ffffff",
                          background: C.primary,
                          borderRadius: 999,
                          padding: "1px 7px",
                          marginLeft: 4,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 18,
                          height: 18,
                          lineHeight: 1,
                        }}
                      >
                        {inboxUnread > 99 ? "99+" : inboxUnread}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <NotificationsButton />
          {!isMobile && <LanguageToggle />}
          {!isMobile && (
            <div
              style={{
                width: 1,
                height: 26,
                background: "#e2e8f0",
                margin: "0 4px",
              }}
            />
          )}
          {!isMobile && (
            <AvatarMenu onSignOutClick={() => setShowSignOutModal(true)} />
          )}
          {isMobile && <HamburgerButton onClick={() => setDrawerOpen(true)} />}
        </div>
      </header>

      {isMobile && drawerOpen && (
        <MobileDrawer
          navItems={navItems}
          activeFor={activeFor}
          isAdmin={isAdmin}
          onClose={() => setDrawerOpen(false)}
          onSignOutClick={() => {
            setDrawerOpen(false);
            setShowSignOutModal(true);
          }}
        />
      )}

      {showSignOutModal && (
        <SignOutModal
          onClose={() => setShowSignOutModal(false)}
          onConfirm={() => {
            setShowSignOutModal(false);
            signOut();
            navigate("/");
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// Hamburger button + slide-in drawer
// =============================================================================

function HamburgerButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open menu"
      style={iconButtonStyle}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={C.textDark}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  );
}

function MobileDrawer({
  navItems,
  activeFor,
  isAdmin,
  onClose,
  onSignOutClick,
}) {
  const { user: localUser, signOut } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const { lang, setLanguage } = useLanguage();
  const [showProfileModal, setShowProfileModal] = useState(false);

  const name = getDisplayName(localUser);
  const initials = getInitials(name);
  const officerLogo =
    localUser?.organisationOfficer?.organisation?.logoDownloadUrl ||
    localUser?.institutionOfficer?.institution?.logoDownloadUrl ||
    localUser?.professional?.avatarDownloadUrl ||
    localUser?.avatarDownloadUrl;

  return (
    <>
      {/* Backdrop. Tapping it closes the drawer. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          border: 0,
          padding: 0,
          cursor: "pointer",
          zIndex: 1000,
        }}
      />
      {/* Panel -- slides in from the right. min(...) so it never exceeds
          the viewport on narrow phones. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(320px, 86vw)",
          background: C.bg,
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 16px rgba(0,0,0,0.12)",
          overflowY: "auto",
          fontFamily: font,
        }}
      >
        {/* Header with user info + close button */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${C.divider}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
              flex: 1,
            }}
          >
            <AvatarSmall initials={initials} photoUrl={officerLogo} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.textDark,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: C.textLight,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {localUser?.email}
              </div>
              {isAdmin && (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.amber,
                    background: C.amberLight,
                    padding: "2px 8px",
                    borderRadius: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {t("nav.admin")}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{
              ...iconButtonStyle,
              width: 32,
              height: 32,
              fontSize: 18,
              color: C.textMed,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        {/* Nav items -- vertical, full-width tap targets */}
        <nav style={{ padding: "8px 0", flex: 1 }}>
          {navItems.map((item) => {
            const active = activeFor(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                style={{
                  display: "block",
                  padding: "14px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: active ? C.primary : C.textStrong,
                  textDecoration: "none",
                  background: active ? C.primaryTint : "transparent",
                  borderLeft: active
                    ? `3px solid ${C.primary}`
                    : "3px solid transparent",
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer actions */}
        <div style={{ borderTop: `1px solid ${C.divider}`, padding: "8px 0" }}>
          {/* Language picker -- two inline buttons */}
          <div
            style={{
              padding: "12px 20px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>
              Language
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                onClick={() => setLanguage("en")}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  border: 0,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontFamily: font,
                  background: lang === "en" ? C.blueLight : "transparent",
                  color: lang === "en" ? C.blue : C.textMed,
                }}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLanguage("ta")}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  border: 0,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontFamily: font,
                  background: lang === "ta" ? C.blueLight : "transparent",
                  color: lang === "ta" ? C.blue : C.textMed,
                }}
              >
                த
              </button>
            </div>
          </div>

          {(localUser?.type === "INSTITUTION_OFFICER" ||
            localUser?.type === "ORGANISATION_OFFICER") && (
            <button
              type="button"
              onClick={() => {
                onClose();
                setShowProfileModal(true);
              }}
              style={drawerItemButtonStyle}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Customize Profile
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              navigate("/change-password");
              onClose();
            }}
            style={drawerItemButtonStyle}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Change password
          </button>

          <button
            type="button"
            onClick={() => {
              onClose();
              if (onSignOutClick) {
                onSignOutClick();
              } else {
                signOut();
                navigate("/");
              }
            }}
            style={drawerItemButtonStyle}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t("common.signOut")}
          </button>
        </div>
      </aside>

      {showProfileModal && (
        <OrganisationProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          initialData={
            localUser?.type === "INSTITUTION_OFFICER"
              ? localUser?.institutionOfficer?.institution
              : localUser?.organisationOfficer?.organisation
          }
          isInstitution={localUser?.type === "INSTITUTION_OFFICER"}
        />
      )}
    </>
  );
}

const drawerItemButtonStyle = {
  width: "100%",
  padding: "12px 20px",
  background: "transparent",
  border: 0,
  textAlign: "left",
  fontSize: 13,
  fontWeight: 600,
  color: C.textDark,
  cursor: "pointer",
  fontFamily: font,
  display: "flex",
  alignItems: "center",
  gap: 10,
};

// =============================================================================
// Notifications button (icon + unread count badge + popover with the user's
// recent notifications). Polls the unread count every 60s.
// =============================================================================

export function resolveNotificationDestination(item, userType) {
  if (!item) return null;
  const type = item.type;
  const metadata = item.metadata || {};
  let link = item.link || "";

  const isVerifier =
    userType === "INSTITUTION_OFFICER" || userType === "ORGANISATION_OFFICER";

  // Verifiers navigating to /billing or subscription expiring should go to /earnings
  if (isVerifier && (link === "/billing" || type === "MEMBERSHIP_EXPIRING")) {
    return "/earnings";
  }

  if (type === "VERIFICATION_REQUEST_RECEIVED") {
    const reqId = metadata.requestId;
    return reqId
      ? `/inbox?tab=PENDING&request=${reqId}`
      : link || "/inbox?tab=PENDING";
  }

  if (type === "VERIFICATION_APPROVED") {
    const credId = metadata.credentialId;
    return credId ? `/profile?credential=${credId}` : link || "/profile";
  }

  if (type === "REQUEST_MESSAGE") {
    const reqId = metadata.requestId;
    if (isVerifier) {
      return reqId ? `/inbox?thread=${reqId}` : link || "/inbox";
    }
    return reqId ? `/verifications?thread=${reqId}` : link || "/verifications";
  }

  if (type === "VERIFICATION_REJECTED") {
    const reqId = metadata.requestId;
    return reqId
      ? `/verifications?tab=REJECTED&request=${reqId}`
      : link || "/verifications?tab=REJECTED";
  }

  if (type === "JOIN_REQUEST_RECEIVED") {
    return "/officers?tab=JOIN_REQUESTS";
  }

  if (type === "OFFICER_INVITE") {
    return "/officers";
  }

  if (type === "CANDIDATE_PROFILE_REQUEST") {
    return "/candidates";
  }

  if (type === "PROFILE_VIEWED") {
    return "/profile";
  }

  if (type === "EMPLOYMENT_ENDED") {
    const credId = metadata.credentialId;
    return credId ? `/profile?credential=${credId}` : link || "/profile";
  }

  if (type === "PAYMENT_RECEIVED") {
    return isVerifier ? "/earnings" : link || "/billing";
  }

  if (type === "MEMBERSHIP_EXPIRING") {
    return isVerifier ? "/earnings" : link || "/billing";
  }

  return link || null;
}

function NotificationsButton() {
  const navigate = useNavigate();
  const { localUser, user } = useAuth();
  const userType = (localUser || user)?.type;
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const ref = useRef(null);

  // Click-outside-to-close
  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Poll unread count every 60 seconds. Cheap call that just returns a number.
  useEffect(() => {
    let cancelled = false;
    async function fetchCount() {
      try {
        const { unread: count } = await fetchUnreadCount();
        if (!cancelled) setUnread(count ?? 0);
      } catch {
        // Silently fail - non-critical
      }
    }
    fetchCount();
    const handle = setInterval(fetchCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  // When the popover opens, fetch the recent list
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchNotifications(15)
      .then(({ items: list, unread: count }) => {
        setItems(list);
        setUnread(count ?? 0);
        setLoadError(null);
      })
      .catch((err) =>
        setLoadError(err.message ?? "Your notifications couldn't be loaded."),
      )
      .finally(() => setLoading(false));
  }, [open]);

  async function handleClick(item) {
    setOpen(false);
    if (!item.readAt) {
      // Optimistic UI update
      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id ? { ...x, readAt: new Date().toISOString() } : x,
        ),
      );
      setUnread((u) => Math.max(0, u - 1));
      try {
        await markNotificationRead(item.id);
      } catch {
        // Not saved: show it unread again rather than pretend.
        setItems((prev) =>
          prev.map((x) => (x.id === item.id ? { ...x, readAt: null } : x)),
        );
        setUnread((u) => u + 1);
      }
    }
    const dest = resolveNotificationDestination(item, userType);
    if (dest) navigate(dest);
  }

  async function handleMarkAllRead() {
    const before = { items, unread };
    setItems((prev) =>
      prev.map((x) =>
        x.readAt ? x : { ...x, readAt: new Date().toISOString() },
      ),
    );
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      // Not saved: put back what was unread.
      setItems(before.items);
      setUnread(before.unread);
    }
  }
  // Shake continuously when there are unread notifications until the menu is opened (or when hovered)
  const shouldShake = (unread > 0 && !open) || hovered;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <style>{`
        @keyframes bellShake {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(14deg); }
          30% { transform: rotate(-14deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-10deg); }
          75% { transform: rotate(4deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Notifications"
        aria-label={
          unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
        }
        aria-expanded={open}
        style={{
          ...iconButtonStyle,
          position: "relative",
          background: hovered || open ? C.surface : "transparent",
          borderRadius: 10,
          padding: 8,
          transition: "all 0.2s ease",
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke={hovered || open ? C.textDark : C.textMed}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transformOrigin: "top center",
            transform: shouldShake ? "scale(1.15)" : "scale(1)",
            animation: shouldShake
              ? "bellShake 0.65s ease-in-out infinite"
              : "none",
            transition: "stroke 0.2s ease, fill 0.2s ease, transform 0.2s ease",
          }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Dot appears ONLY when new notification arrives (unread > 0) */}
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              width: 9,
              height: 9,
              background: C.primary,
              borderRadius: "50%",
              boxShadow: `0 0 0 2px ${C.bg}`,
            }}
          />
        )}
      </button>

      {open && (
        <div
          style={{
            ...popoverStyle,
            // On 360px screens the popover used to push the page wider than
            // the viewport. Cap to whatever fits, with the original 340/380
            // bounds as the ceiling.
            minWidth: "min(340px, calc(100vw - 32px))",
            maxWidth: "min(380px, calc(100vw - 16px))",
            maxHeight: 480,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${C.divider}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textDark }}>
              Notifications
              {unread > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: "1px 6px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.red,
                    background: C.redLight,
                    borderRadius: 8,
                  }}
                >
                  {unread} unread
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "transparent",
                  border: 0,
                  color: C.primary,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: font,
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div
                style={{
                  padding: "24px 16px",
                  fontSize: 13,
                  color: C.textMuted,
                  textAlign: "center",
                }}
              >
                Loading...
              </div>
            ) : loadError ? (
              <div
                role="alert"
                style={{
                  padding: "24px 16px",
                  fontSize: 13,
                  color: C.red,
                  textAlign: "center",
                }}
              >
                {loadError}
              </div>
            ) : items.length === 0 ? (
              <div
                style={{
                  padding: "32px 16px",
                  fontSize: 13,
                  color: C.textMuted,
                  textAlign: "center",
                }}
              >
                No notifications yet.
              </div>
            ) : (
              items.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  onClick={() => handleClick(item)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationItem({ item, onClick }) {
  const isUnread = !item.readAt;
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "12px 16px",
        background: isUnread ? C.primaryTint : "transparent",
        border: 0,
        borderBottom: `1px solid ${C.divider}`,
        textAlign: "left",
        cursor: "pointer",
        fontFamily: font,
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      {/* Unread dot indicator */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: isUnread ? C.primary : "transparent",
          marginTop: 6,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.textStrong,
            marginBottom: 2,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: C.textMuted,
            lineHeight: 1.4,
            marginBottom: 4,
          }}
        >
          {item.body}
        </div>
        <div style={{ fontSize: 10, color: C.textSubtle }}>
          {formatRelative(item.createdAt)}
        </div>
      </div>
    </button>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

// =============================================================================
// Language toggle
// =============================================================================

function LanguageToggle() {
  const { lang, setLanguage } = useLanguage();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const isActive = hovered || open;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Change Language"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: isActive ? "#eff6ff" : "#f8fafc",
          border: `1px solid ${isActive ? "#93c5fd" : "#e2e8f0"}`,
          borderRadius: 10,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 700,
          color: isActive ? "#2563eb" : C.textDark,
          letterSpacing: 0.5,
          fontFamily: font,
          transition: "all 0.2s ease",
          boxShadow: isActive ? "0 2px 8px rgba(37, 99, 235, 0.12)" : "none",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={isActive ? "#2563eb" : "#64748b"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transition: "stroke 0.2s ease" }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span>{lang === "en" ? "EN" : "த"}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{ ...popoverStyle, minWidth: 160, padding: 6 }}>
          <button
            onClick={() => {
              setLanguage("en");
              setOpen(false);
            }}
            style={{
              ...menuItemStyle(lang === "en"),
              borderRadius: 8,
              padding: "8px 12px",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              EN - {t("nav.languageEnglish")}
            </span>
            {lang === "en" && <CheckIconSmall />}
          </button>
          <button
            onClick={() => {
              setLanguage("ta");
              setOpen(false);
            }}
            style={{
              ...menuItemStyle(lang === "ta"),
              borderRadius: 8,
              padding: "8px 12px",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              த - {t("nav.languageTamil")}
            </span>
            {lang === "ta" && <CheckIconSmall />}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Avatar menu
// =============================================================================

function AvatarMenu({ onSignOutClick }) {
  const { user: localUser, signOut } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const ref = useRef(null);
  // An officer's yearly subscription, read from the server when the menu opens.
  const subscription = usePrice(
    open ? subscriptionTypeFor(localUser?.type) : null,
  );

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const name = getDisplayName(localUser);
  const initials = getInitials(name);
  const officerLogo =
    localUser?.organisationOfficer?.organisation?.logoDownloadUrl ||
    localUser?.institutionOfficer?.institution?.logoDownloadUrl ||
    localUser?.professional?.avatarDownloadUrl ||
    localUser?.avatarDownloadUrl;

  const roleText = (() => {
    if (localUser?.type === "PLATFORM_ADMIN") return "Administrator";
    if (localUser?.type === "INSTITUTION_OFFICER") {
      return officerRoleLabel(localUser);
    }
    if (localUser?.type === "ORGANISATION_OFFICER") {
      const branchName = localUser?.organisationOfficer?.branch?.name;
      const orgName = localUser?.organisationOfficer?.organisation?.name;
      if (branchName) {
        return `${orgName || "Organisation"} - ${branchName}`;
      }
      return officerRoleLabel(localUser);
    }
    if (localUser?.type === "PROFESSIONAL" || localUser?.professional) {
      return localUser?.professional?.isStudent
        ? t("profile.student") || "Student"
        : t("profile.professional") || "Professional";
    }
    return "Professional";
  })();

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: open ? "#f1f5f9" : "transparent",
          border: 0,
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          gap: 10,
          transition: "all 0.15s ease",
        }}
      >
        <AvatarSmall initials={initials} photoUrl={officerLogo} />
        <div
          style={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.2,
              letterSpacing: "-0.2px",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "#64748b",
              marginTop: 2,
            }}
          >
            {roleText}
          </div>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#64748b"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            marginLeft: 4,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{ ...popoverStyle, minWidth: 220, right: 0 }}>
          {/* <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${C.divider}`,
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 700, color: C.textDark }}>{name}</div>
            <div
              style={{
                fontSize: 11,
                color: C.textLight,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {localUser?.email}
            </div>
          </div> */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${C.divider}`,
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 700, color: C.textDark }}>{name}</div>
            <div
              style={{
                fontSize: 11,
                color: C.textLight,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {localUser?.email}
            </div>

            {/* Annual subscription price: only for institutions and organisations */}
            {subscription.pricePaise && (
              <div
                style={{
                  marginTop: 8,
                  padding: "4px 8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#166534",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Annual subscription:</span>
                <span style={{ fontWeight: 700 }}>
                  {formatRupees(subscription.pricePaise)}
                </span>
              </div>
            )}
          </div>

          {(localUser?.type === "INSTITUTION_OFFICER" ||
            localUser?.type === "ORGANISATION_OFFICER") && (
            <button
              onClick={() => {
                setOpen(false);
                setShowProfileModal(true);
              }}
              style={menuItemStyle(false)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Customize Profile
            </button>
          )}

          <button
            onClick={() => {
              navigate("/change-password");
              setOpen(false);
            }}
            style={menuItemStyle(false)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Change password
          </button>
          <button
            onClick={() => {
              setOpen(false);
              if (onSignOutClick) {
                onSignOutClick();
              } else {
                setShowSignOutModal(true);
              }
            }}
            style={menuItemStyle(false)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t("common.signOut")}
          </button>
        </div>
      )}

      {showSignOutModal && (
        <SignOutModal
          onClose={() => setShowSignOutModal(false)}
          onConfirm={() => {
            setShowSignOutModal(false);
            signOut();
            navigate("/");
          }}
        />
      )}

      {showProfileModal && (
        <OrganisationProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          initialData={
            localUser?.type === "INSTITUTION_OFFICER"
              ? localUser?.institutionOfficer?.institution
              : localUser?.organisationOfficer?.organisation
          }
          isInstitution={localUser?.type === "INSTITUTION_OFFICER"}
        />
      )}
    </div>
  );
}

function AvatarSmall({ initials, photoUrl }) {
  const [loadFailed, setLoadFailed] = useState(false);
  const showPhoto = !loadFailed && isDisplayableUrl(photoUrl);

  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        background: "#0f172a",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13.5,
        fontWeight: 800,
        letterSpacing: -0.3,
        border: "2px solid #2563eb",
        boxShadow: "0 0 0 2px #dbeafe",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt={initials || "Profile"}
          onError={() => setLoadFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initials || "RA"
      )}
    </div>
  );
}

function CheckIconSmall() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.blue}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginLeft: "auto" }}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// =============================================================================
// helpers
// =============================================================================

/** Which subscription an account's organisation pays, or null for none. */
function subscriptionTypeFor(userType) {
  if (userType === "INSTITUTION_OFFICER") return "INSTITUTION_SUBSCRIPTION";
  if (userType === "ORGANISATION_OFFICER") return "ORGANISATION_SUBSCRIPTION";
  return null;
}

function getDisplayName(localUser) {
  if (localUser?.professional)
    return `${localUser.professional.firstName} ${localUser.professional.lastName}`;
  if (localUser?.institutionOfficer?.fullName)
    return localUser.institutionOfficer.fullName;
  if (localUser?.organisationOfficer?.fullName)
    return localUser.organisationOfficer.fullName;
  if (localUser?.platformAdmin?.fullName)
    return localUser.platformAdmin.fullName;
  return localUser?.email ?? "User";
}

function getInitials(name) {
  return (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("");
}

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  background: "transparent",
  border: 0,
  borderRadius: 6,
  cursor: "pointer",
  color: C.textMed,
  fontFamily: font,
};

const popoverStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  minWidth: 180,
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  zIndex: 100,
  overflow: "hidden",
};

const menuItemStyle = (active) => ({
  width: "100%",
  padding: "10px 16px",
  background: active ? C.blueLight : "transparent",
  border: 0,
  textAlign: "left",
  fontSize: 13,
  fontWeight: 600,
  color: active ? C.blue : C.textDark,
  cursor: "pointer",
  fontFamily: font,
  display: "flex",
  alignItems: "center",
  gap: 8,
});

// =============================================================================
// Sign Out Confirmation Modal
// =============================================================================

function SignOutModal({ onClose, onConfirm }) {
  const [hoverNo, setHoverNo] = useState(false);
  const [hoverYes, setHoverYes] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 400,
          padding: "32px 28px",
          textAlign: "center",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#fef2f2",
            color: "#dc2626",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            border: "1px solid #fee2e2",
          }}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </div>

        <h2
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: C.navy,
            margin: "0 0 8px",
            letterSpacing: "-0.3px",
          }}
        >
          Are you sure you want to sign out?
        </h2>
        <p
          style={{
            fontSize: 13.5,
            color: C.textMed,
            lineHeight: 1.5,
            margin: "0 0 24px",
          }}
        >
          You will need to sign back in to access your portal and account.
        </p>

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => setHoverNo(true)}
            onMouseLeave={() => setHoverNo(false)}
            style={{
              flex: 1,
              padding: "10px 20px",
              borderRadius: 8,
              border: `1px solid ${hoverNo ? C.primary : C.dividerStrong || "#cbd5e1"}`,
              background: hoverNo ? C.primary : "#fff",
              color: hoverNo ? "#fff" : C.textDark,
              fontSize: 13.5,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: hoverNo ? "0 4px 12px rgba(8, 131, 62, 0.25)" : "none",
            }}
          >
            No
          </button>
          <button
            type="button"
            onClick={onConfirm}
            onMouseEnter={() => setHoverYes(true)}
            onMouseLeave={() => setHoverYes(false)}
            style={{
              flex: 1,
              padding: "10px 20px",
              borderRadius: 8,
              border: `1px solid ${hoverYes ? "#b91c1c" : "#dc2626"}`,
              background: hoverYes ? "#b91c1c" : "#dc2626",
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: hoverYes
                ? "0 6px 16px rgba(220, 38, 38, 0.4)"
                : "0 2px 8px rgba(220, 38, 38, 0.25)",
            }}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}
